import { MantineProvider } from '@mantine/core';
import { emotionTransform, MantineEmotionProvider } from '@mantine/emotion';
import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
} from 'react';

import { emotionCache } from '../emotion/cache';
import { cssVariablesResolver, theme } from './theme';

export type ColorScheme = 'light' | 'dark';

/** The scheme the dashboard ships in. There is no user-facing theme control. */
export const APP_COLOR_SCHEME: ColorScheme = 'dark';

const SchemeContext = createContext<ColorScheme>(APP_COLOR_SCHEME);

/** The scheme ThemeProvider is applying, for values classes cannot express. */
export const useColorScheme = () => useContext(SchemeContext);

interface ThemeProviderProps {
  scheme: ColorScheme;
  children: ReactNode;
}

/**
 * The single boundary keeping the Tailwind scope, browser `color-scheme` and
 * Mantine scheme on one value; the class goes on the document element so
 * portalled content resolves the same tokens.
 */
export const ThemeProvider = ({ scheme, children }: ThemeProviderProps) => {
  useLayoutEffect(() => {
    const { classList } = document.documentElement;
    classList.toggle('dark', scheme === 'dark');
    classList.toggle('light', scheme === 'light');
  }, [scheme]);

  return (
    <SchemeContext value={scheme}>
      <MantineEmotionProvider cache={emotionCache}>
        <MantineProvider
          stylesTransform={emotionTransform}
          // Mantine applies forceColorScheme in a post-paint effect; without a
          // matching defaultColorScheme the first painted frame runs light.
          defaultColorScheme={scheme}
          forceColorScheme={scheme}
          theme={theme}
          cssVariablesResolver={cssVariablesResolver}
        >
          {children}
        </MantineProvider>
      </MantineEmotionProvider>
    </SchemeContext>
  );
};
ThemeProvider.displayName = 'ThemeProvider';
